/**
 * React Query hooks for the Chat module
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { chatApi, CreateSessionRequest, UpdateSessionRequest, PromptConfig } from '../services/chatApi';

const QUERY_KEYS = {
  sessions: (chatType?: string) => ['chat', 'sessions', chatType || 'all'] as const,
  session: (id: string) => ['chat', 'sessions', 'detail', id] as const,
  sessionsByEntity: (entityRef: string) => ['chat', 'sessions', 'by-entity', entityRef] as const,
  promptConfig: ['chat', 'settings', 'promptConfig'] as const,
};

/** List chat sessions filtered by type (defaults to 'assistant' for AI Chat page) */
export function useChatSessions(chatType: 'assistant' | 'agent' | 'avatar' = 'assistant') {
  return useQuery({
    queryKey: QUERY_KEYS.sessions(chatType),
    queryFn: () => chatApi.listSessions(chatType),
  });
}

/** Get a single chat session with messages */
export function useChatSessionDetail(sessionId: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.session(sessionId || 'none'),
    queryFn: () => chatApi.getSession(sessionId!),
    enabled: !!sessionId,
  });
}

/** Get chat sessions linked to a specific entity (backlinks) */
export function useChatSessionsByEntity(entityRef: string | null) {
  return useQuery({
    queryKey: QUERY_KEYS.sessionsByEntity(entityRef || 'none'),
    queryFn: () => chatApi.getSessionsByEntity(entityRef!),
    enabled: !!entityRef,
  });
}

/** Create a new chat session */
export function useCreateSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateSessionRequest = {}) => chatApi.createSession(request),
    onSuccess: (result) => {
      // Invalidate the sessions list for the created chat type
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessions(result.data.chat_type) });
      // Also invalidate entity-specific session lists so history dropdown updates
      queryClient.invalidateQueries({ queryKey: ['chat', 'sessions', 'by-entity'] });
    },
  });
}

/** Delete a chat session */
export function useDeleteSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => chatApi.deleteSession(sessionId),
    onSuccess: () => {
      // Invalidate all session lists since we don't know the type
      queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] });
    },
  });
}

/** Archive a chat session */
export function useArchiveSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => chatApi.archiveSession(sessionId),
    onSuccess: () => {
      // Invalidate all session lists
      queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] });
    },
  });
}

/** Unarchive a chat session */
export function useUnarchiveSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => chatApi.unarchiveSession(sessionId),
    onSuccess: () => {
      // Invalidate all session lists
      queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] });
    },
  });
}

/** Update session (title, model, MCP config) */
export function useUpdateSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: UpdateSessionRequest }) =>
      chatApi.updateSession(sessionId, data),
    // Optimistic update: patch session detail cache immediately so the picker
    // doesn't flicker between old → new value during the request roundtrip.
    onMutate: async ({ sessionId, data }) => {
      const detailKey = QUERY_KEYS.session(sessionId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData(detailKey);
      queryClient.setQueryData(detailKey, (old: { data?: Record<string, unknown> } | undefined) => {
        if (!old?.data) return old;
        const patch: Record<string, unknown> = {};
        if ('model_category' in data) patch.model_category = data.model_category;
        if ('title' in data && data.title !== undefined) patch.title = data.title;
        return { ...old, data: { ...old.data, ...patch } };
      });
      return { previous, detailKey };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(ctx.detailKey, ctx.previous);
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.session(variables.sessionId) });
    },
  });
}

/** Search across chat sessions */
export function useChatSearch(query: string) {
  return useQuery({
    queryKey: ['chat', 'search', query],
    queryFn: () => chatApi.searchSessions(query),
    enabled: query.length >= 2,
  });
}

/** Update session title */
export function useUpdateSessionTitle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, title }: { sessionId: string; title: string }) =>
      chatApi.updateSessionTitle(sessionId, title),
    onSuccess: (_, variables) => {
      // Invalidate all session lists and the specific session detail
      queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.session(variables.sessionId) });
    },
  });
}

/** Send a message (non-streaming) */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, message }: { sessionId: string; message: string }) =>
      chatApi.sendMessage(sessionId, message),
    onSuccess: (_, variables) => {
      // Invalidate session detail and sessions list (title/order may have changed)
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.session(variables.sessionId) });
      queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] });
    },
  });
}

// ============== User Settings Hooks ==============

/** Get user's prompt config with available parts and presets */
export function usePromptConfig() {
  return useQuery({
    queryKey: QUERY_KEYS.promptConfig,
    queryFn: () => chatApi.getPromptConfig(),
  });
}

/** Update user's prompt config */
export function useUpdatePromptConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (promptConfig: PromptConfig) => chatApi.updatePromptConfig(promptConfig),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.promptConfig });
    },
  });
}
